import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Form, Button, Row, Col, Accordion, Card, Badge, Spinner, Alert } from 'react-bootstrap';
import { Star, Mail, Filter, Plus, MoreHorizontal, Briefcase, Building2, MapPin, DollarSign, Calendar, User, Users, BarChart3, AlertCircle, Eye } from 'lucide-react';
import { getUsers } from '../../../../../services/UserSerivce.js';
import { getContacts } from '../../../../../services/contactServices.js';
import { axiosInstanceV1 } from '../../../../../services/AxiosInstance.js';
import { getDepartmentsV2 } from '../../../../../services/CourseModule';
import CommentsSection from '../../../commentSection/CommentSection.jsx';

import {
  getOrganizations,
  updateOrganization,
} from "../../../../../services/organizationServices.js";
import OrganizationPopupView from "../../../organization/organizationpopup/OrganizationPopupView.jsx";
import { getAppliedStudentsForJob } from "../../../../../services/placementServices.js";
import ContactPopupView from "../../../contacts/contactpopup/ContactPopupView.jsx";
import Swal from "sweetalert2";

const PlacementSummaryTab = ({
  placement,
  onHide,
  onUpdatePlacement,
  commentOn,
  handleViewStudent,
  formatDate,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedOrganizationForPopup, setSelectedOrganizationForPopup] = useState(null);
  const [showOrganizationPopup, setShowOrganizationPopup] = useState(false);
  const [defaultActiveOrgTab, setDefaultActiveOrgTab] = useState("summary");

  const [selectedContactForPopup, setSelectedContactForPopup] = useState(null);
  const [showContactPopup, setShowContactPopup] = useState(false);
  const [defaultActiveContactTab, setDefaultActiveContactTab] = useState("summary");

  // Paginated applied students state
  const [paginatedStudents, setPaginatedStudents] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalStudents, setTotalStudents] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadingInitial, setLoadingInitial] = useState(false);
  const studentsContainerRef = useRef(null);
  const isLoadingMoreRef = useRef(false);
  const lastLoadedId = useRef(null);
  const placementIdRef = useRef(null);
  const initialLoadDoneRef = useRef(false);

  const PAGE_SIZE = 20;

  // Load applied students with pagination
  const loadAppliedStudentsPaginated = useCallback(async (page, isInitial = false) => {
    const currentPlacementId = placementIdRef.current;
    if (!currentPlacementId) return;
    
    if (isInitial) {
      setLoadingInitial(true);
    } else {
      setLoadingMore(true);
    }
    
    try {
      const response = await getAppliedStudentsForJob(currentPlacementId, page, PAGE_SIZE);
      const data = response?.data || [];
      const total = response?.total || response?.data?.length || 0;
      
      // Handle different response structures
      let studentsData = [];
      let totalCount = 0;
      
      if (Array.isArray(data)) {
        studentsData = data;
        totalCount = total;
      } else if (data?.data && Array.isArray(data.data)) {
        studentsData = data.data;
        totalCount = data.total || data.data.length;
      } else if (data?.students && Array.isArray(data.students)) {
        studentsData = data.students;
        totalCount = data.total || data.students.length;
      } else {
        studentsData = [];
        totalCount = 0;
      }
      
      if (isInitial) {
        setPaginatedStudents(studentsData);
        setCurrentPage(page);
      } else {
        setPaginatedStudents(prev => [...prev, ...studentsData]);
        setCurrentPage(page);
      }
      
      setTotalStudents(totalCount);
      setHasMore(studentsData.length === PAGE_SIZE && (page * PAGE_SIZE) < totalCount);
      
    } catch (err) {
      console.error("Failed to load applied students:", err);
      setError("Failed to load applied students: " + err.message);
      if (isInitial) {
        setPaginatedStudents([]);
      }
      setHasMore(false);
    } finally {
      if (isInitial) {
        setLoadingInitial(false);
      } else {
        setLoadingMore(false);
        isLoadingMoreRef.current = false;
      }
    }
  }, [PAGE_SIZE]);

  // Initial load when placement changes - Fixed to prevent multiple calls
  useEffect(() => {
    const newPlacementId = placement?.id;
    
    // Check if placement ID actually changed
    if (newPlacementId && newPlacementId !== placementIdRef.current) {
      // console.log("🔄 Placement ID changed in Summary Tab:", newPlacementId);
      placementIdRef.current = newPlacementId;
      lastLoadedId.current = newPlacementId;
      initialLoadDoneRef.current = false;
      
      // Reset infinite scroll state
      setPaginatedStudents([]);
      setCurrentPage(1);
      setHasMore(true);
      setTotalStudents(0);
      
      // Trigger initial load
      loadAppliedStudentsPaginated(1, true);
      initialLoadDoneRef.current = true;
    }
  }, [placement?.id, loadAppliedStudentsPaginated]);

  // Handle scroll to load more
  const handleScroll = useCallback(() => {
    if (!studentsContainerRef.current || !hasMore || loadingMore || isLoadingMoreRef.current) {
      return;
    }

    const container = studentsContainerRef.current;
    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;
    
    // Load more when user scrolls to bottom (with 100px threshold)
    if (scrollTop + clientHeight >= scrollHeight - 100) {
      isLoadingMoreRef.current = true;
      const nextPage = currentPage + 1;
      loadAppliedStudentsPaginated(nextPage, false);
    }
  }, [hasMore, loadingMore, currentPage, loadAppliedStudentsPaginated]);

  // Add scroll event listener
  useEffect(() => {
    const container = studentsContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => {
        container.removeEventListener('scroll', handleScroll);
      };
    }
  }, [handleScroll]);

  // ── Safe field readers — all API fields are now arrays of objects ─────────

  // assignedToId: [{firstName, lastName, id}] or {firstName, lastName, id}
  const getAssignedUser = () => {
    const raw = placement?.assignedToId || placement?.assignedToIds || placement?.assignedTo;
    if (!raw) return null;
    if (Array.isArray(raw) && raw.length > 0) return raw[0];
    if (typeof raw === "object" && (raw.firstName || raw.name)) return raw;
    return null;
  };

  // contactId: [{name, id}] or {name, id}
  const getContact = () => {
    const raw = placement?.contactId;
    if (!raw) return null;
    if (Array.isArray(raw) && raw.length > 0) return raw[0];
    if (typeof raw === "object" && raw.name) return raw;
    return null;
  };

  // organizationId: [{name, id}] or scalar id string
  const getOrganizationId = () => {
    const raw = placement?.organizationId;
    if (!raw) return null;
    if (Array.isArray(raw) && raw.length > 0) return raw[0].id;
    if (typeof raw === "object" && raw.id) return raw.id;
    if (typeof raw === "string") return raw;
    return null;
  };

  const getOrganizationName = () => {
    const raw = placement?.organizationId;
    if (!raw) return placement?.organizationName || "N/A";
    if (Array.isArray(raw) && raw.length > 0)
      return raw[0].name || placement?.organizationName || "N/A";
    if (typeof raw === "object" && raw.name) return raw.name;
    return placement?.organizationName || "N/A";
  };

  // department: [{name, id}] or scalar
  const getDepartmentDisplay = () => {
    const raw = placement?.department;
    if (!raw) return "N/A";
    if (Array.isArray(raw) && raw.length > 0) return raw[0].name || "N/A";
    if (typeof raw === "object" && raw.name) return raw.name;
    if (typeof raw === "string") return raw;
    return "N/A";
  };



  // studentIds count — still a comma-separated string in some responses
  const getStudentCount = () => {
    // Use totalStudents from paginated response
    if (totalStudents > 0) {
      return `${totalStudents} student${totalStudents !== 1 ? "s" : ""}`;
    }
    // Try coursewiseStudentIds + batchwiseStudentIds arrays first (populated response)
    const courseStudents = Array.isArray(placement?.coursewiseStudentIds)
      ? placement.coursewiseStudentIds.length
      : 0;
    const batchStudents = Array.isArray(placement?.batchwiseStudentIds)
      ? placement.batchwiseStudentIds.length
      : 0;
    if (courseStudents > 0 || batchStudents > 0) {
      const total = courseStudents + batchStudents;
      return `${total} student${total !== 1 ? "s" : ""}`;
    }
    // Fallback: comma-separated string
    if (!placement?.studentIds) return "0 students";
    const studentArray = placement.studentIds
      .split(",")
      .filter((id) => id.trim());
    const count = studentArray.length;
    return `${count} student${count !== 1 ? "s" : ""}`;
  };

  const handleViewOrganization = (organizationId) => {
    const rawOrg = placement?.organizationId;
    const orgObj = Array.isArray(rawOrg) && rawOrg.length > 0 ? rawOrg[0] : (typeof rawOrg === 'object' ? rawOrg : null);
    
    if (orgObj && (orgObj.id === organizationId || orgObj._id === organizationId || !organizationId)) {
      setSelectedOrganizationForPopup(orgObj);
      setDefaultActiveOrgTab("summary");
      setShowOrganizationPopup(true);
    } else if (placement?.organizationName && !organizationId) {
       // Handle case where we only have name but no ID in the click (though getOrganizationId should provide it)
       setSelectedOrganizationForPopup({ name: placement.organizationName, id: organizationId });
       setDefaultActiveOrgTab("summary");
       setShowOrganizationPopup(true);
    }
  };

  const handleViewContact = (contactId) => {
    const rawContact = getContact();
    if (rawContact && (rawContact.id === contactId || rawContact._id === contactId || !contactId)) {
      setSelectedContactForPopup(rawContact);
      setDefaultActiveContactTab("summary");
      setShowContactPopup(true);
    }
  };

  const handleCloseContactPopup = () => {
    setShowContactPopup(false);
    setSelectedContactForPopup(null);
    setDefaultActiveContactTab("summary");
  };

  const handleUpdateContact = async (updatedContact) => {
    try {
      setSelectedContactForPopup(updatedContact);
    } catch (error) {
      console.error("Update failed:", error);
    }
  };

  const handleCloseOrganizationPopup = () => {
    setShowOrganizationPopup(false);
    setSelectedOrganizationForPopup(null);
    setDefaultActiveOrgTab("summary");
  };

  const handleUpdateOrganization = async (updatedOrganization) => {
    try {
      await updateOrganization(updatedOrganization.id, updatedOrganization);
      setSelectedOrganizationForPopup(updatedOrganization);
    } catch (error) {
      console.error("Update failed:", error);
    }
  };

  const handleCommentsError = (err) => {
    setError(err);
  };

  const handleShowAllCourses = (courses) => {
    Swal.fire({
      title: 'Assigned Courses',
      html: `
        <div style="text-align: left; max-height: 400px; overflow-y: auto; padding: 10px;">
          <ul style="list-style-type: none; padding-left: 0; margin: 0;">
            ${courses.map(c => {
              const displayName = typeof c === 'object' ? (c.name || c.label || c.title || 'Unnamed Course') : c;
              return `
                <li style="padding: 12px 0; border-bottom: 1px solid #f0f0f0; display: flex; align-items: center; color: #333;">
                  <span style="color: #ea580c; margin-right: 12px; font-size: 1.2rem; line-height: 1;">•</span>
                  <span style="font-size: 0.95rem; font-weight: 500;">${displayName}</span>
                </li>`;
            }).join('')}
          </ul>
        </div>
      `,
      confirmButtonText: 'Close',
      confirmButtonColor: '#ea580c',
    });
  };

  if (isLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "400px" }}
      >
        <div className="text-center">
          <Spinner animation="border" variant="primary" className="mb-3" />
          <p className="text-muted">Loading placement details...</p>
        </div>
      </div>
    );
  }

  if (!placement) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "400px" }}
      >
        <div className="text-center">
          <div className="text-muted">
            <Briefcase size={48} className="mb-3 opacity-50" />
            <p className="mb-0">No placement data available</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" dismissible onClose={() => setError(null)}>
        <AlertCircle size={16} className="me-2" />
        {error}
      </Alert>
    );
  }

  // Resolved values — safe strings/objects, never raw arrays/objects passed to JSX
  const assignedUser = getAssignedUser();
  const contact = getContact();
  const orgId = getOrganizationId();
  const orgName = getOrganizationName();
  const departmentLabel = getDepartmentDisplay();
  const studentCount = getStudentCount();

  const displayedStudents = paginatedStudents.slice(0, paginatedStudents.length);

  const formatShortDate = (dateString) => {
    if (!dateString) return "N/A";
    const d = new Date(dateString);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = String(d.getFullYear()).slice(2);
    return `${day}/${month}/${year}`;
  };

  return (
    <div className="placement-summary-content">
      <Row className="h-100" style={{ margin: 0 }}>
        {/* Left Column - Main Details */}
        <Col
          md={9}
          style={{
            borderRight: "1px solid #e9ecef",
            paddingRight: "20px",
            maxHeight: "700px",
            overflowY: "auto",
          }}
        >
          <Row className="placement-header-row">
            <Col md={2}>
              <span className="field-label">Assigned To*</span>
            </Col>
            <Col md={2}>
              <span className="field-label">Organization</span>
            </Col>
            <Col md={3}>
              <span className="field-label">Contact Person</span>
            </Col>
            <Col md={3}>
              <span className="field-label">Department</span>
            </Col>
            <Col md={2}>
              <span className="field-label">Location</span>
            </Col>
          </Row>

          <Row className="placement-data-row">
            {/* Assigned To */}
            <Col md={2}>
              {assignedUser ? (
                <div className="assigned-user">
                  <div className="user-avatar">
                    {assignedUser.firstName || assignedUser.lastName
                      ? `${assignedUser.firstName?.substring(0, 1) || ""}${assignedUser.lastName?.substring(0, 1) || ""}`.toUpperCase() || "U"
                      : (assignedUser.name || "U").substring(0, 2).toUpperCase()}
                  </div>
                  <span className="user-name">
                    {assignedUser.firstName || assignedUser.lastName
                      ? `${assignedUser.firstName || ""} ${assignedUser.lastName || ""}`.trim().toLowerCase()
                      : (assignedUser.name || "N/A").toLowerCase()}
                  </span>
                </div>
              ) : (
                <span className="field-value text-muted">Not assigned</span>
              )}
            </Col>

            {/* Organization */}
            <Col md={2}>
              <span
                className="field-value"
                style={{
                  color: orgId ? "#0d6efd" : "#212529",
                  cursor: orgId ? "pointer" : "default",
                  fontWeight: orgId ? "500" : "normal",
                }}
                onClick={() => {
                  if (orgId) handleViewOrganization(orgId);
                }}
              >
                {orgName}
              </span>
            </Col>

            {/* Contact Person */}
            <Col md={3}>
              {contact ? (
                <div>
                  <span className="field-value">
                    {contact.name}
                  </span>
                  {contact.email && (
                    <div className="email-value">
                      <span style={{ color: "#ea580c", fontSize: "0.8rem" }}>{contact.email}</span>
                    </div>
                  )}
                </div>
              ) : (
                <span className="field-value text-muted">Not specified</span>
              )}
            </Col>

            {/* Department */}
            <Col md={3}>
              <span className="field-value">{departmentLabel}</span>
            </Col>

            {/* Location */}
            <Col md={2}>
              <span className="field-value">
                {placement.jobLocation || "N/A"}
              </span>
            </Col>
          </Row>

          <Row className="placement-additional-row mt-3">
            <Col md={2}>
              <span className="field-label">Salary Range</span>
              <div className="field-value">
                {placement.salary || "Not specified"}
              </div>
            </Col>
            <Col md={2}>
              <span className="field-label">Course</span>
              <div className="field-value">
                {placement?.courseModuleIds && placement.courseModuleIds.length > 0 ? (
                  <div className="d-flex align-items-center">
                    <span className="text-truncate" style={{ maxWidth: '120px' }} title={placement.courseModuleIds[0].name}>
                      {placement.courseModuleIds[0].name}
                    </span>
                    {placement.courseModuleIds.length > 1 && (
                      <span 
                        className="ms-1 badge bg-light text-primary border" 
                        style={{ cursor: 'pointer', fontSize: '0.75rem' }}
                        onClick={() => handleShowAllCourses(placement.courseModuleIds)}
                      >
                        +{placement.courseModuleIds.length - 1}
                      </span>
                    )}
                  </div>
                ) : "Not specified"}
              </div>
            </Col>
            <Col md={2}>
              <span className="field-label">Students Assigned</span>
              <div className="field-value">{studentCount}</div>
            </Col>
            <Col md={2}>
              <span className="field-label">Job Status</span>
              <div className="field-value">
                <span
                  className={`badge ${
                    placement.jobPositionStatus === "open"
                      ? "bg-primary"
                      : placement.jobPositionStatus === "in-progress"
                        ? "bg-warning"
                        : placement.jobPositionStatus === "hired"
                          ? "bg-success"
                          : placement.jobPositionStatus === "rejected"
                            ? "bg-danger"
                            : "bg-secondary"
                  }`}
                >
                  {placement.jobPositionStatus || "N/A"}
                </span>
              </div>
            </Col>
            <Col md={2}>
              <span className="field-label">Posted Date</span>
              <div className="field-value">
                {placement.jobPostedDate
                  ? formatShortDate(placement.jobPostedDate)
                  : "N/A"}
              </div>
            </Col>
            <Col md={2} className="mt-md-0 mt-3">
              <span className="field-label">Apply Deadline</span>
              <div className="field-value">
                {placement.endDate
                  ? formatShortDate(placement.endDate)
                  : "N/A"}
              </div>
            </Col>
            <Col md={2}>
              <span className="field-label">Status</span>
              <div className="field-value">
                <span
                  className={`badge ${placement.status === "active" ? "bg-success" : "bg-secondary"}`}
                >
                  {placement.status || "N/A"}
                </span>
              </div>
            </Col>
          </Row>

          <div className="job-description-section">
            <h6>Job Description</h6>
            <div className="job-details">
              {placement.jobDescription ? (
                <div
                  className="job-description-html"
                  dangerouslySetInnerHTML={{ __html: placement.jobDescription }}
                />
              ) : (
                <div className="text-muted">
                  <p>
                    <strong>Job Position:</strong>{" "}
                    {placement.jobName?.split("(")[0]?.trim()}
                  </p>
                  <p>
                    <strong>Organization:</strong> {orgName}
                  </p>
                  <p>
                    <strong>Location:</strong> {placement.jobLocation}
                  </p>
                  <p>
                    <strong>Salary:</strong> {placement.salary}
                  </p>
                  <p>
                    <strong>Department:</strong> {departmentLabel}
                  </p>
                </div>
              )}
              <Button variant="link" className="view-more-btn">
                View more
              </Button>
            </div>
          </div>

          <div className="activity-timeline-section mt-4 mb-4">
            <h6>Activity Timeline</h6>
            <div className="timeline-container p-3 bg-white border rounded">
              <div className="timeline-item d-flex align-items-start mb-3">
                <div className="timeline-marker bg-success rounded-circle mt-2 me-3" style={{ width: '10px', height: '10px' }}></div>
                <div className="flex-grow-1">
                  <div className="fw-bold" style={{ fontSize: '0.9rem' }}>Job Created</div>
                  <small className="text-muted">{placement.createdAt ? (formatDate ? formatDate(placement.createdAt) : new Date(placement.createdAt).toLocaleDateString()) : 'N/A'}</small>
                </div>
              </div>
              
              {placement.updatedAt && placement.updatedAt !== placement.createdAt && (
                <div className="timeline-item d-flex align-items-start mb-3">
                  <div className="timeline-marker bg-info rounded-circle mt-2 me-3" style={{ width: '10px', height: '10px' }}></div>
                  <div className="flex-grow-1">
                    <div className="fw-bold" style={{ fontSize: '0.9rem' }}>Last Updated</div>
                    <small className="text-muted">{formatDate ? formatDate(placement.updatedAt) : new Date(placement.updatedAt).toLocaleDateString()}</small>
                  </div>
                </div>
              )}

              <div className="timeline-item d-flex align-items-start">
                <div className="timeline-marker bg-primary rounded-circle mt-2 me-3" style={{ width: '10px', height: '10px' }}></div>
                <div className="flex-grow-1">
                  <div className="fw-bold" style={{ fontSize: '0.9rem' }}>Current Status</div>
                  <div className="d-flex align-items-center mt-1">
                    <span className={`badge me-2 ${placement.status === 'active' ? 'bg-success' : 'bg-secondary'}`}>
                      {placement.status || 'N/A'}
                    </span>
                    <span className="text-muted small">{studentCount} assigned</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {placement.id && (
            <CommentsSection
              entityId={placement.id}
              type="jobprofiles"
              title={`Discussion for ${placement.jobName}`}
              users={[]}
              onError={handleCommentsError}
              showCollapse={true}
              defaultExpanded={true}
              maxHeight="500px"
              className="job-comments-section"
            />
          )}
        </Col>

        {/* Right Column - Applied Students with Scroll Pagination */}
        <Col
          md={3}
          style={{
            paddingLeft: "20px",
            maxHeight: "700px",
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          <div className="applied-students-sidebar">
            <h6 className="mb-3" style={{ fontWeight: 600, fontSize: "16px" }}>
              <Users size={18} className="me-2" style={{ display: "inline" }} />
              Applied Students
              {totalStudents > 0 && (
                <Badge bg="primary" className="ms-2">
                  {totalStudents}
                </Badge>
              )}
            </h6>

            {loadingInitial ? (
              <div className="text-center py-4">
                <Spinner animation="border" variant="primary" size="sm" />
                <p className="text-muted mt-2" style={{ fontSize: "0.85em" }}>
                  Loading students...
                </p>
              </div>
            ) : paginatedStudents && paginatedStudents.length > 0 ? (
              <div>
                <div className="mb-3">
                  <small className="text-muted">
                    Showing {paginatedStudents.length} of {totalStudents} students
                  </small>
                </div>
                <div
                  ref={studentsContainerRef}
                  className="applied-students-list"
                  style={{ maxHeight: "500px", overflowY: "auto" }}
                >
                  {paginatedStudents.map((student, index) => (
                    <div
                      key={student.id || index}
                      className="student-card"
                      style={{
                        border: "1px solid #e9ecef",
                        borderRadius: "6px",
                        padding: "12px",
                        marginBottom: "10px",
                        backgroundColor: "#f8f9fa",
                        transition: "all 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#e9ecef";
                        e.currentTarget.style.boxShadow =
                          "0 2px 4px rgba(0,0,0,0.1)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "#f8f9fa";
                        e.currentTarget.style.boxShadow = "none";
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                        }}
                      >
                        <div>
                          <div
                            className="student-title"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewStudent && handleViewStudent(student);
                            }}
                            style={{
                              fontWeight: 600,
                              marginBottom: "4px",
                              fontSize: "0.9em",
                              cursor: "pointer",
                              color: "#007bff"
                            }}
                          >
                            {student.studentName || student.name || "N/A"}
                          </div>
                        </div>
                        <Button
                          variant="link"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewStudent && handleViewStudent(student);
                          }}
                          className="p-0"
                          style={{ color: "#ea580c" }}
                        >
                          <Eye size={14} />
                        </Button>
                      </div>
                      {(student.studentEmail || student.email) && (
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#999",
                            marginBottom: "4px",
                            wordBreak: "break-word",
                          }}
                        >
                          {student.studentEmail || student.email}
                        </div>
                      )}
                      {student.createdAt && (
                        <div style={{ fontSize: "11px", color: "#999" }}>
                          Applied:{" "}
                          {formatDate
                            ? formatDate(student.createdAt)
                            : new Date(student.createdAt).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {/* Loading indicator for more data */}
                  {loadingMore && (
                    <div className="text-center py-3">
                      <Spinner animation="border" variant="primary" size="sm" />
                      <p className="text-muted mt-1" style={{ fontSize: "0.75em" }}>
                        Loading more students...
                      </p>
                    </div>
                  )}
                  
                  {/* End of list indicator */}
                  {!hasMore && paginatedStudents.length > 0 && (
                    <div className="text-center py-3">
                      <small className="text-muted">End of list</small>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-4" style={{ color: "#6c757d" }}>
                <Users size={32} className="mb-2" style={{ opacity: 0.5 }} />
                <p style={{ fontSize: "14px" }}>No Applied Students</p>
              </div>
            )}
          </div>
        </Col>
      </Row>

      {selectedOrganizationForPopup && (
        <OrganizationPopupView
          show={showOrganizationPopup}
          onHide={handleCloseOrganizationPopup}
          organization={selectedOrganizationForPopup}
          onUpdateOrganization={handleUpdateOrganization}
          defaultActiveTab={defaultActiveOrgTab}
        />
      )}

      {selectedContactForPopup && (
        <ContactPopupView
          isVisible={showContactPopup}
          onClose={handleCloseContactPopup}
          contact={selectedContactForPopup}
          onUpdate={handleUpdateContact}
          defaultActiveTab={defaultActiveContactTab}
          users={[]}
        />
      )}

      <style>{`
        .placement-summary-content { padding: 20px 0; }
        .placement-header-row { border-bottom: 1px solid #e9ecef; padding-bottom: 10px; margin-bottom: 15px; }
        .placement-data-row { margin-bottom: 20px; }
        .placement-additional-row { border-top: 1px solid #e9ecef; padding-top: 20px; margin-bottom: 30px; }
        .field-label { font-size: 0.85rem; color: #6c757d; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; }
        .field-value { font-size: 0.9rem; color: #212529; margin-top: 5px; font-weight: 500; }
        .assigned-user { display: flex; align-items: center; gap: 8px; }
        .user-avatar { width: 32px; height: 32px; border-radius: 50%; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); display: flex; align-items: center; justify-content: center; color: white; font-weight: bold; font-size: 0.8rem; }
        .user-name { font-size: 0.9rem; color: #495057; }
        .email-value { margin-top: 2px; }
        .email-value a { color: #ea580c; text-decoration: none; font-size: 0.8rem; }
        .email-value a:hover { text-decoration: underline; }
        .job-description-section { margin: 30px 0; padding: 20px; background: #f8f9fa; border-radius: 8px; }
        .job-description-section h6 { color: #495057; margin-bottom: 15px; font-weight: 600; }
        .job-description-html { background: white; padding: 20px; border-radius: 6px; border: 1px solid #e9ecef; margin-bottom: 15px; line-height: 1.6; }
        .view-more-btn { color: #ea580c; padding: 0; font-size: 0.9rem; }
        .job-comments-section { border: 2px solid #e3f2fd; background: linear-gradient(135deg, #fafffe 0%, #f8fffe 100%); }
        .badge { font-size: 0.75rem; padding: 0.25rem 0.5rem; }
        .applied-students-sidebar { background: white; border: 1px solid #e9ecef; border-radius: 8px; padding: 15px; height: 100%; }
        .applied-students-list { scroll-behavior: smooth; }
        .student-card { transition: all 0.2s ease; }
        .student-card:hover { transform: translateY(-2px); }
        .student-title { font-weight: 600; margin-bottom: 4px; font-size: 0.9em; }
        .activity-timeline-section h6 { color: #495057; margin-bottom: 15px; font-weight: 600; }
        .timeline-container { border: 1px solid #e9ecef; }
        .timeline-item { position: relative; }
        .timeline-marker { flex-shrink: 0; z-index: 1; }
      `}</style>
    </div>
  );
};

export default PlacementSummaryTab;